/**
 * ═══════════════════════════════════════════════════════════════
 * Hasımoğlu Çiftliği – Gelişmiş Analitik Panosu
 * ───────────────────────────────────────────────────────────────
 * Süper Adminler için Recharts tabanlı görsel şölen ekranı.
 * ═══════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { collection, query, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { 
  Users, Beef, CreditCard, Building2, TrendingUp, AlertCircle, Loader2, MessageCircle, Tag, Download, FileText
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { exportDashboardToExcel } from '../../services/exportService';
import BranchSummaryTemplate from '../../components/shared/BranchSummaryTemplate';
import { canViewBranchData } from '../../utils/branchUtils';
import { 
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend
} from 'recharts';

export default function AdminDashboard() {
  const { isSuperAdmin, userProfile, systemSettings } = useAuth();
  const navigate = useNavigate();

  const [animals, setAnimals] = useState([]);
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [activeParentTab, setActiveParentTab] = useState('all');
  const [activeSubTab, setActiveSubTab] = useState('all');
  const [showSummaryModal, setShowSummaryModal] = useState(false);

  useEffect(() => {
    // 1. Hayvanları Çek
    const systemBranches = systemSettings?.systemBranches || [];
    const qAnimals = query(collection(db, 'animals'));
    const unSubAnimals = onSnapshot(qAnimals, (snapshot) => {
      const aData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // Super admin isen hepsini, alt adminsen kendi ve alt şubelerinin verilerini gör
      const filteredAnimals = isSuperAdmin ? aData : aData.filter(a => canViewBranchData(userProfile, a.branch, systemBranches));
      setAnimals(filteredAnimals);
    });

    // 2. Hisseleri Çek (Tahsilat/Finans verileri hissede tutulduğu için yeterlidir)
    const qShares = query(collection(db, 'shares'));
    const unSubShares = onSnapshot(qShares, (snapshot) => {
      const sData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setShares(sData);
      setLoading(false);
    });

    return () => {
      unSubAnimals();
      unSubShares();
    };
  }, [isSuperAdmin, userProfile]);

  
  const systemBranches = systemSettings?.systemBranches || [];
  
  // Sadece hayvanları olan "Ana Şubeler" (parentId boş olanlar)
  const activeBranchIds = new Set(animals.map(a => a.branch).filter(Boolean));
  // Ana şubeleri tanımla
  const mainBranches = systemBranches.filter(b => !b.parentId && (activeBranchIds.has(b.id) || systemBranches.some(sub => sub.parentId === b.id && activeBranchIds.has(sub.id))));
  
  // Seçili ana şubenin alt şubeleri
  const currentSubBranches = activeParentTab !== 'all' 
    ? systemBranches.filter(b => b.parentId === activeParentTab && activeBranchIds.has(b.id))
    : [];

  // Aktif Sekmeye Göre Filtreleme
  const filteredAnimals = animals.filter(a => {
    if (activeParentTab === 'all') return true;
    
    const branchObj = systemBranches.find(b => b.id === a.branch);
    const belongsToParent = a.branch === activeParentTab || branchObj?.parentId === activeParentTab;
    
    if (!belongsToParent) return false;
    
    if (activeSubTab !== 'all') {
      return a.branch === activeSubTab;
    }
    
    return true;
  });


  const filteredAnimalIds = new Set(filteredAnimals.map(a => a.id));
  const filteredShares = shares.filter(s => filteredAnimalIds.has(s.animalId));

  // --- HESAPLAMALAR ---
  const totalAnimalsCount = filteredAnimals.length;
  
  let totalAvailableCapacity = 0;
  let totalSoldCapacity = 0;
  let expectedRevenue = 0;

  filteredAnimals.forEach(a => {
    const ts = Number(a.totalShares) || 7;
    const sc = Number(a.shareCount) || 0;
    totalAvailableCapacity += ts;
    totalSoldCapacity += sc;
    expectedRevenue += Number(a.price || 0);
  });
  const emptyCapacity = totalAvailableCapacity - totalSoldCapacity;

  let collectedRevenue = 0;
  let totalDiscountAmount = 0;
  let totalShareTargetPrice = 0;
  const animalCollectedMap = {};
  
  filteredShares.forEach(s => {
    const paid = Number(s.paidAmount || 0);
    const disc = Number(s.discountAmount || 0);
    const assignedPrice = Number(s.price || 0);
    collectedRevenue += paid;
    totalDiscountAmount += disc;
    totalShareTargetPrice += assignedPrice;
    if (!animalCollectedMap[s.animalId]) animalCollectedMap[s.animalId] = 0;
    animalCollectedMap[s.animalId] += paid;
  });
  
  // Asıl Merkez Borcu = expectedRevenue
  // Tahsil Edilen = collectedRevenue
  const maxPossibleRevenue = expectedRevenue - totalDiscountAmount;
  // Müşterilerden kalan net alacak = Sadece KAYITLI müşterilerin ödeme taahhüdü - Tahsilatlar
  const customerDebt = totalShareTargetPrice - collectedRevenue; 
  const remainingDebt = expectedRevenue - collectedRevenue; // Merkeze kapatılması gereken tutar

  // Yeni Modül Hesaplamaları
  const animalDebtsList = filteredAnimals.map(a => {
    const totalCollected = animalCollectedMap[a.id] || 0;
    const debt = Number(a.price || 0) - totalCollected;
    return { ...a, remainingDebt: debt, totalCollected };
  }).filter(a => a.remainingDebt > 0).sort((a, b) => b.remainingDebt - a.remainingDebt).slice(0, 5);

  const shareholderDebtsList = filteredShares.map(s => {
    const totalSharePrice = Number(s.price || 0);
    const paid = Number(s.paidAmount || 0);
    const debt = totalSharePrice - paid;
    return { ...s, remainingDebt: debt };
  }).filter(s => s.remainingDebt > 0 && s.customerFirstName && String(s.customerFirstName).trim() !== '')
  .sort((a, b) => b.remainingDebt - a.remainingDebt).slice(0, 10);

  const handleWhatsAppReminder = (share) => {
    const fullName = `${share.customerFirstName || ''} ${share.customerLastName || ''}`.trim();
    const message = `Sayın ${fullName}, Hasımoğlu Çiftliği kurban hisseniz ile ilgili ${share.remainingDebt.toLocaleString('tr-TR')} ₺ kalan ödemenizi hatırlatmak isteriz. Bize ulaşarak veya panel üzerinden ödemenizi gerçekleştirebilirsiniz.`;
    const phone = share.customerPhone?.replace(/[^0-9]/g, '') || '';
    let waPhone = phone;
    if (waPhone.startsWith('0')) waPhone = waPhone.substring(1);
    if (!waPhone.startsWith('90') && waPhone.length === 10) waPhone = '90' + waPhone;
    window.open(`https://wa.me/${waPhone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const branchName = activeParentTab === 'all' 
          ? 'TumSistem' 
          : mainBranches.find(b => b.id === activeParentTab)?.name || 'Sube';
      
      await exportDashboardToExcel(filteredAnimals, filteredShares, branchName);
    } catch (error) {
      alert("Excel oluşturulurken hata: " + error.message);
    } finally {
      setIsExporting(false);
    }
  };

  // --- GRAFİK VERİLERİ ---
  const financeData = [
    { name: 'Tahsil Edilen', value: collectedRevenue, color: '#34d399' }, // forest-400 equivalent
    { name: 'Kalan Alacak', value: remainingDebt > 0 ? remainingDebt : 0, color: '#ef4444' } // danger-500 equivalent
  ];

  const capacityData = [
    {
      name: 'Hisse Durumu',
      'Dolu Hisse (Satılan)': totalSoldCapacity,
      'Boş Hisse (Kalan)': emptyCapacity
    }
  ];

  const myBranches = systemSettings?.systemBranches?.filter(b => canViewBranchData(userProfile, b.id, systemSettings.systemBranches)) || [];
  const branchNames = [...new Set(myBranches.map(b => b.name))].join(', ');
  
  const dashboardTitleText = isSuperAdmin 
    ? 'Yönetim Özeti (Tüm Şubeler)' 
    : branchNames 
      ? `Yönetim Özeti (${branchNames})` 
      : 'Yönetim Özeti';

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[500px] text-forest-500">
        <Loader2 className="w-12 h-12 animate-spin mb-4" />
        <p className="text-white font-medium animate-pulse">Sistem Verileri Yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in zoom-in-95 duration-500">
      
      {/* ── ÜST BAŞLIK VE SEKME (TAB) MENÜSÜ ── */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between w-full md:w-auto md:flex-1 gap-4">
          <div>
            <h2 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-forest-400" />
              {dashboardTitleText}
            </h2>
            <p className="text-sm text-navy-400 mt-1">
              Şubelere ait kurban, müşteri ve finansal özetinizi canlı (real-time) izleyin.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button 
              onClick={() => setShowSummaryModal(true)}
              disabled={filteredAnimals.length === 0}
              className="flex items-center gap-2 bg-gradient-to-r from-navy-700 to-navy-900 hover:from-navy-600 hover:to-navy-800 text-white px-5 py-2.5 rounded-xl font-bold shadow-lg shadow-navy-900/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed border border-navy-500/30 whitespace-nowrap"
            >
              <FileText className="w-5 h-5" />
              Özet Belge
            </button>
            <button 
              onClick={handleExport}
              disabled={isExporting || filteredAnimals.length === 0}
              className="flex items-center gap-2 bg-gradient-to-r from-emerald-600 to-forest-600 hover:from-emerald-500 hover:to-forest-500 text-white px-5 py-2.5 rounded-xl font-bold shadow-lg shadow-emerald-900/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed border border-emerald-400/30 whitespace-nowrap"
            >
              {isExporting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
              {isExporting ? 'Hazırlanıyor...' : 'Excel İndir'}
            </button>
          </div>
        </div>

        
          {/* Ana Şube Selector */}
          {mainBranches.length > 0 && (
            <div className="flex flex-col gap-2 relative">
              <div className="flex items-center gap-2 bg-navy-900/60 p-1.5 rounded-xl border border-navy-700/50 backdrop-blur-md overflow-x-auto custom-scrollbar">
                <button
                  onClick={() => { setActiveParentTab('all'); setActiveSubTab('all'); }}
                  className={`px-4 py-2 rounded-lg text-sm font-bold whitespace-nowrap transition-all ${activeParentTab === 'all' ? 'bg-forest-600 text-white shadow-lg' : 'text-navy-300 hover:text-white hover:bg-navy-800'}`}
                >
                  TÜM SİSTEM
                </button>
                {mainBranches.map(branch => {
                  return (
                    <button
                      key={branch.id}
                      onClick={() => { setActiveParentTab(branch.id); setActiveSubTab('all'); }}
                      className={`px-4 py-2 rounded-lg text-sm font-bold whitespace-nowrap transition-all flex items-center gap-2 ${activeParentTab === branch.id ? 'bg-navy-700 text-gold-400 shadow-lg border border-navy-600' : 'text-navy-400 hover:text-white hover:bg-navy-800'}`}
                    >
                      <Building2 className="w-3.5 h-3.5" />
                      {branch.name.toUpperCase()}
                    </button>
                  );
                })}
              </div>
              
              {/* Alt Şube Selector */}
              {activeParentTab !== 'all' && currentSubBranches.length > 0 && (
                <div className="flex items-center gap-2 bg-navy-800/40 p-1.5 rounded-xl border border-navy-600/30 overflow-x-auto custom-scrollbar">
                  <span className="text-xs font-semibold text-navy-400 pl-2 pr-1">Alt Filreler:</span>
                  <button
                    onClick={() => setActiveSubTab('all')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-all ${activeSubTab === 'all' ? 'bg-navy-600 text-white shadow' : 'text-navy-300 hover:text-white hover:bg-navy-700'}`}
                  >
                    Kümülatif Toplam (Merkez + Altlar)
                  </button>
                  
                  {/* Merkez için de bir buton (Sadece kendine ait hayvanları görmek isterse) */}
                  <button
                    onClick={() => setActiveSubTab(activeParentTab)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-all flex items-center gap-1.5 ${activeSubTab === activeParentTab ? 'bg-navy-700 border border-gold-500/50 text-gold-400 shadow' : 'text-navy-400 hover:text-white hover:bg-navy-700'}`}
                  >
                     Sadece {mainBranches.find(b => b.id === activeParentTab)?.name} Merkez
                  </button>

                  {currentSubBranches.map(sub => (
                    <button
                      key={sub.id}
                      onClick={() => setActiveSubTab(sub.id)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-all flex items-center gap-1.5 ${activeSubTab === sub.id ? 'bg-navy-700 border border-cyan-500/50 text-cyan-400 shadow' : 'text-cyan-600/70 hover:text-cyan-400 hover:bg-navy-700'}`}
                    >
                      {sub.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Merkez Borcu */}
        <div className="glass rounded-xl p-4 border-l-4 border-l-navy-400 relative overflow-hidden group hover:bg-navy-800/50 transition-colors">
          <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20"><Building2 className="w-10 h-10 text-navy-400" /></div>
          <p className="text-xs font-semibold text-navy-300">Merkezin Asıl Fiyatı</p>
          <p className="text-xl font-bold text-white mt-1">{expectedRevenue.toLocaleString('tr-TR')} ₺</p>
          <p className="text-[10px] text-navy-400 mt-1 leading-tight">Şubeye kesilen toplam hayvan maliyeti (Gerçek Borç)</p>
        </div>

        {/* Uygulanan İndirim */}
        <div className="glass rounded-xl p-4 border-l-4 border-l-warning-500 bg-warning-900/10 relative overflow-hidden group hover:bg-warning-900/20 transition-colors">
          <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20"><Tag className="w-10 h-10 text-warning-500" /></div>
          <p className="text-xs font-semibold text-warning-400">Şubenin Yaptığı İndirimler</p>
          <p className="text-xl font-bold text-warning-400 mt-1">{totalDiscountAmount.toLocaleString('tr-TR')} ₺</p>
          <p className="text-[10px] text-warning-500/80 mt-1 leading-tight">Şubenin bu kadar tutarı kendi zararından/kârından karşılaması gerekecek.</p>
        </div>

        {/* Toplanabilecek Max Para */}
        <div className="glass rounded-xl p-4 border-l-4 border-l-blue-400 relative overflow-hidden group hover:bg-navy-800/50 transition-colors">
          <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20"><Users className="w-10 h-10 text-blue-400" /></div>
          <p className="text-xs font-semibold text-blue-300">Hedeflenen Satış Geliri</p>
          <p className="text-xl font-bold text-blue-400 mt-1">{maxPossibleRevenue.toLocaleString('tr-TR')} ₺</p>
          <p className="text-[10px] text-navy-400 mt-1 leading-tight">Müşterilerden maksimum toplanabilecek para (Merkez - İndirimler)</p>
        </div>

        {/* Kasaya Giren (Tahsilat) */}
        <div className="glass rounded-xl p-4 border-l-4 border-l-gold-500 relative overflow-hidden group hover:bg-navy-800/50 transition-colors">
          <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20"><CreditCard className="w-10 h-10 text-gold-500" /></div>
          <p className="text-xs font-semibold text-gold-400">Kasaya Giren Nakit</p>
          <p className="text-xl font-bold text-gold-400 mt-1">{collectedRevenue.toLocaleString('tr-TR')} ₺</p>
          <p className="text-[10px] text-navy-400 mt-1 leading-tight">Şu ana kadar müşterilerden alınan tahsilat.</p>
        </div>

        {/* Kalan Alacak */}
        <div className="glass rounded-xl p-4 border-l-4 border-l-danger-500 relative overflow-hidden group hover:bg-navy-800/50 transition-colors">
          <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20"><AlertCircle className="w-10 h-10 text-danger-500" /></div>
          <p className="text-xs font-semibold text-danger-400">Müşterilerden Kalan Alacak</p>
          <p className="text-xl font-bold text-danger-400 mt-1">{customerDebt > 0 ? customerDebt.toLocaleString('tr-TR') : 0} ₺</p>
          <p className="text-[10px] text-danger-300/70 mt-1 leading-tight">Bugün kasaya girmesi beklenen açık bakiye tutarı.</p>
        </div>
      </div>

      {/* ── RECHARTS GRAFİKLERİ YAN YANA ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* KASA - FİNANS PASTA GRAFİĞİ & BORÇ ORANI İKİZ PANEL */}
        <div className="glass rounded-2xl p-6 border border-navy-700/50 flex flex-col h-full hover:shadow-xl hover:shadow-navy-900/50 transition-all duration-500">
          <h3 className="text-lg font-bold text-white w-full border-b border-navy-700/50 pb-3 mb-4 flex items-center justify-between">
             <span>Finansal Sağlık Durumu & Maliyet Analizi</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 h-full gap-4 items-center">
             
             {/* SOL KISIM: Pasta Grafik */}
             <div className="flex flex-col items-center md:border-r md:border-navy-700/50 h-full min-h-[300px]">
                {(expectedRevenue === 0) ? (
                   <div className="flex-1 flex items-center justify-center text-navy-400 text-sm">
                      Satış/Finans kaydı henüz yok.
                   </div>
                ) : (
                   <>
                     <div className="w-full relative flex-1 min-h-[220px]">
                       <ResponsiveContainer width="100%" height="100%">
                         <PieChart>
                           <Pie
                             data={financeData}
                             cx="50%" cy="50%"
                             innerRadius={70}
                             outerRadius={100}
                             paddingAngle={5}
                             dataKey="value"
                             animationBegin={0}
                             animationDuration={1500}
                           >
                             {financeData.map((entry, index) => (
                               <Cell key={`cell-${index}`} fill={entry.color} />
                             ))}
                           </Pie>
                           <Tooltip 
                             formatter={(value) => `${value.toLocaleString('tr-TR')} ₺`}
                             contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', borderRadius: '12px', color: '#fff' }}
                             itemStyle={{ color: '#fff', fontWeight: 'bold' }}
                           />
                         </PieChart>
                       </ResponsiveContainer>
                       {/* Ortedeki Yazı */}
                       <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none mt-2">
                         <span className="text-[10px] text-navy-400 font-semibold mb-0.5">Beklenen Toplam</span>
                         <span className="text-lg font-bold text-white">{expectedRevenue.toLocaleString('tr-TR')} ₺</span>
                       </div>
                     </div>
                     <div className="flex items-center justify-center gap-4 mt-2 mb-2 w-full">
                        <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-emerald-400"></div><span className="text-xs text-navy-300">Tahsil Edilen</span></div>
                        <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-danger-500"></div><span className="text-xs text-navy-300">Açık Alacak</span></div>
                     </div>
                   </>
                )}
             </div>

             {/* SAĞ KISIM: Merkez Maliyet Oranı */}
             {(() => {
                const centerDebt = expectedRevenue;
                const maxCollection = maxPossibleRevenue;
                const ratio = maxCollection > 0 ? (centerDebt / maxCollection) * 100 : 0;
                
                return (
                   <div className="flex flex-col justify-center items-center md:pl-4 py-4 md:py-0 h-full">
                      <div className="relative group">
                        <div className={`absolute -inset-1.5 rounded-full opacity-70 blur-md transition-all duration-1000 ${ratio > 100 ? 'bg-danger-500' : 'bg-gold-500'}`}></div>
                        <div className="w-28 h-28 rounded-full bg-navy-950 border border-navy-700 flex flex-col items-center justify-center relative shadow-inner z-10">
                           <span className={`text-3xl font-extrabold ${ratio > 100 ? 'text-danger-400' : 'text-gold-400'}`}>{ratio.toFixed(1)}<span className="text-lg">%</span></span>
                           <span className="text-[9px] text-navy-400 uppercase font-bold tracking-widest mt-1">Maliyet Oranı</span>
                        </div>
                      </div>
                      
                      <div className="mt-6 space-y-3 w-full">
                         <div className="bg-navy-900/50 rounded-lg p-2.5 border border-navy-700/50 flex justify-between items-center group-hover:border-danger-500/50 transition-colors">
                            <span className="text-xs text-navy-300 font-semibold">Merkez Maliyeti</span>
                            <span className="text-sm font-extrabold text-white">{centerDebt.toLocaleString('tr-TR')} ₺</span>
                         </div>
                         <div className="bg-navy-900/50 rounded-lg p-2.5 border border-navy-700/50 flex justify-between items-center group-hover:border-gold-500/50 transition-colors">
                            <span className="text-xs text-navy-300 font-semibold">Toplanacak Max <span className="text-[8px] opacity-60">(İn. Sonrası)</span></span>
                            <span className="text-sm font-extrabold text-gold-400">{maxCollection.toLocaleString('tr-TR')} ₺</span>
                         </div>
                      </div>

                      {ratio > 100 ? (
                         <div className="mt-3 p-2 rounded bg-danger-500/10 border border-danger-500/30 w-full text-center">
                            <p className="text-[10px] text-danger-400 font-bold">⚠️ DİKKAT: Şube zarar sınırını aştı!</p>
                         </div>
                      ) : (
                         <div className="mt-3 p-2 rounded bg-emerald-500/10 border border-emerald-500/20 w-full text-center">
                            <p className="text-[10px] text-emerald-400/80 font-bold">✓ Kârlılık limiti içerisinde</p>
                         </div>
                      )}
                   </div>
                );
             })()}
          </div>
        </div>

        {/* SATIŞ / KAPASİTE IZGARASI (GRID) */}
        <div className="glass rounded-2xl p-6 border border-navy-700/50 flex flex-col h-full max-h-[400px]">
          <div className="flex flex-col xl:flex-row xl:items-center justify-between w-full border-b border-navy-700/50 pb-3 mb-4 gap-3">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
               Hisse Doluluk ve Satış Oranı
            </h3>
            <div className="flex items-center gap-3 text-sm flex-wrap bg-navy-900/60 px-3 py-1.5 rounded-lg border border-navy-700">
               <span className="text-navy-300 font-medium">Toplam: <span className="font-bold text-white">{totalAvailableCapacity}</span></span>
               <div className="w-1 h-1 rounded-full bg-navy-600"></div>
               <span className="text-navy-300 font-medium">Dolu: <span className="font-bold text-emerald-400">{totalSoldCapacity}</span></span>
               <div className="w-1 h-1 rounded-full bg-navy-600"></div>
               <span className="text-navy-300 font-medium">Boş: <span className="font-bold text-danger-400">{emptyCapacity}</span></span>
            </div>
          </div>
          
          {(filteredAnimals.length === 0) ? (
             <div className="flex-1 flex flex-col items-center justify-center text-navy-400 text-sm opacity-60">
                <Beef className="w-10 h-10 mb-2" />
                Aktif şube/kurban kaydı bulunmuyor.
             </div>
          ) : (
             <div className="w-full flex-1 overflow-y-auto pr-2 custom-scrollbar space-y-2.5">
               {filteredAnimals.sort((a, b) => {
                 const numA = parseInt(a.slaughterNo, 10);
                 const numB = parseInt(b.slaughterNo, 10);
                 const hasSeqA = !isNaN(numA);
                 const hasSeqB = !isNaN(numB);

                 if (hasSeqA && hasSeqB) return numA - numB;
                 if (hasSeqA && !hasSeqB) return -1;
                 if (!hasSeqA && hasSeqB) return 1;

                 const isFullA = (Number(a.shareCount) || 0) >= (Number(a.totalShares) || 7) ? 1 : 0;
                 const isFullB = (Number(b.shareCount) || 0) >= (Number(b.totalShares) || 7) ? 1 : 0;
                 if (isFullA !== isFullB) return isFullA - isFullB;
                 
                 return 0;
               }).map((animal) => {
                 const ts = Number(animal.totalShares) || 7;
                 const sc = Number(animal.shareCount) || 0;
                 const slots = Array.from({ length: ts });
                 
                 return (
                   <div 
                     key={animal.id} 
                     onClick={() => navigate(`/dashboard/kurbanlar?highlight=${animal.id}`)}
                     className="flex flex-col sm:flex-row items-start sm:items-center justify-between bg-navy-900/40 p-3 rounded-xl border border-navy-700/50 hover:bg-navy-800/80 transition-colors gap-3 group cursor-pointer"
                   >
                     <div className="flex flex-wrap items-center gap-2 min-w-[200px]">
                       <span className="bg-navy-950 text-gold-400 font-bold px-2 py-1 rounded-md text-[11px] border border-gold-500/20 shadow-sm whitespace-nowrap">
                         SIRA: {animal.slaughterNo || '-'}
                       </span>
                       <span className="bg-navy-800 text-navy-300 font-bold px-2 py-1 rounded-md text-[11px] border border-navy-600 shadow-sm whitespace-nowrap">
                         HAYVAN NO: {animal.animalNo || '-'}
                       </span>
                       <span className="text-sm font-bold text-white tracking-widest group-hover:text-gold-300 transition-colors whitespace-nowrap">KÜPE: {animal.tagNo}</span>
                     </div>
                     <div className="flex items-center gap-1.5 flex-wrap">
                       {slots.map((_, i) => (
                         <div 
                           key={i} 
                           className={`w-5 h-5 rounded-[4px] transition-all transform hover:scale-110 ${i < sc ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)] border border-emerald-400' : 'bg-danger-600 shadow-[0_0_8px_rgba(220,38,38,0.5)] border border-danger-500'}`}
                           title={i < sc ? 'Dolu Hisse' : 'Boş Hisse'}
                         />
                       ))}
                     </div>
                   </div>
                 );
               })}
             </div>
          )}
        </div>

      </div>

      {/* ── YENİ: FİNANSAL RİSK TABLOLARI ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pb-12 mt-6">
        {/* EN BORÇLU HAYVANLAR */}
        <div className="glass rounded-2xl p-6 border border-danger-500/30">
           <h3 className="text-lg font-bold text-white flex items-center gap-2 border-b border-navy-700/50 pb-3 mb-4">
             <AlertCircle className="w-5 h-5 text-danger-500" />
             Açık Veren Kurbanlıklar (Top 5)
           </h3>
           {animalDebtsList.length === 0 ? (
             <div className="text-navy-400 text-sm text-center py-8">Tüm hayvanların ödemesi alınmış.</div>
           ) : (
             <div className="space-y-3">
               {animalDebtsList.map((animal, i) => (
                 <div key={animal.id} className="bg-navy-900/50 border border-navy-700 rounded-xl p-4 flex justify-between items-center group hover:bg-navy-800 transition-colors">
                    <div className="flex items-center gap-4">
                       <span className="text-xl font-bold text-navy-500">#{i + 1}</span>
                       <div>
                         <p className="text-white font-bold flex items-center gap-2">
                           <span className="bg-forest-500/20 text-forest-400 px-2 py-0.5 rounded text-xs">{animal.animalNo}</span>
                           Küpe: {animal.tagNo}
                         </p>
                         <p className="text-xs text-navy-400 mt-1 flex items-center gap-1">
                           <Building2 className="w-3 h-3" /> {animal.branch?.toUpperCase()} | Toplam: {Number(animal.price).toLocaleString('tr-TR')} ₺
                         </p>
                       </div>
                    </div>
                    <div className="text-right">
                       <p className="text-sm text-navy-300">İçerideki Açık</p>
                       <p className="text-lg font-bold text-danger-500">{animal.remainingDebt.toLocaleString('tr-TR')} ₺</p>
                    </div>
                 </div>
               ))}
             </div>
           )}
        </div>

        {/* KARA LİSTE (EN ÇOK BORCU KALAN 10 KİŞİ) */}
        <div className="glass rounded-2xl p-6 border border-gold-500/30">
           <h3 className="text-lg font-bold text-white flex items-center gap-2 border-b border-navy-700/50 pb-3 mb-4">
             <Users className="w-5 h-5 text-gold-500" />
             Tahsilat Bekleyen Hissedarlar (İlk 10)
           </h3>
           {shareholderDebtsList.length === 0 ? (
             <div className="text-navy-400 text-sm text-center py-8">Açık borcu olan hissedar bulunmuyor.</div>
           ) : (
             <div className="space-y-3">
               {shareholderDebtsList.map((share, i) => (
                 <div key={share.id} className="bg-navy-900/50 border border-navy-700 rounded-xl p-3 flex justify-between items-center hover:border-gold-500/30 transition-all">
                    <div>
                       <p className="text-white font-bold text-sm truncate max-w-[150px] sm:max-w-[200px]">{`${share.customerFirstName || ''} ${share.customerLastName || ''}`.trim()}</p>
                       <p className="text-xs text-navy-400 flex items-center gap-1 mt-0.5"><Tag className="w-3 h-3"/> H:{filteredAnimals.find(a => a.id === share.animalId)?.animalNo || '-'}</p>
                    </div>
                    <div className="flex items-center gap-4">
                       <div className="text-right">
                          <p className="text-[10px] text-navy-300 uppercase font-bold tracking-wider">Kalan Ödeme</p>
                          <p className="text-base font-bold text-danger-400">{share.remainingDebt.toLocaleString('tr-TR')} ₺</p>
                       </div>
                       <button onClick={() => handleWhatsAppReminder(share)} className="bg-green-600/20 text-green-500 hover:bg-green-600 hover:text-white p-2.5 rounded-lg transition-colors shadow-lg" title="WhatsApp Uyarısı Gönder">
                          <MessageCircle className="w-4 h-4" />
                       </button>
                    </div>
                 </div>
               ))}
             </div>
           )}
        </div>
      </div>

      {showSummaryModal && (
        <BranchSummaryTemplate 
           animals={filteredAnimals}
           shares={filteredShares}
           branchName={activeParentTab === 'all' 
             ? 'TÜM ŞUBELER ÖZET RAPORU' 
             : mainBranches.find(b => b.id === activeParentTab)?.name || 'ŞUBE ÖZETİ'
           }
           totalAvailableCapacity={totalAvailableCapacity}
           totalSoldCapacity={totalSoldCapacity}
           emptyCapacity={emptyCapacity}
           expectedRevenue={expectedRevenue}
           collectedRevenue={collectedRevenue}
           maxPossibleRevenue={maxPossibleRevenue}
           onClose={() => setShowSummaryModal(false)}
        />
      )}

    </div>
  );
}
